// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultChartPropsMappingProps.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { DEFAULT_SETTING_VALUE, isChartType } from 'logic/data/Izenda.ReportPartChartConstants';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import {
  isEqual,
  filter,
  get,
  isNil,
  isEmpty,
  includes,
  getOr,
  omitBy,
  memoize,
  find,
  forEach,
  omit,
  flatMap,
  flow,
  has,
  some
} from 'lodash/fp';

export default (reportPartDetails, fieldStore) => {
  const reportPartContent = reportPartDetails.reportPartContent || {};
  const getProperties = key => reportPartContent.getReportPartProperties(key);
  return {
    reportPartDetails,
    getAllChartProps: () => {
      const chartProperties = getProperties();
      return {
        chartOptions: { ...chartProperties, colors: reportPartDetails.getThemeColors(chartProperties) },
        seriesOptions: reportPartDetails.fieldCollection,
        chartData: chartDataParser(reportPartContent.activeElmsByContainers, reportPartDetails.expandedLevel, {
          fieldsMapping: reportPartDetails.previewDataFieldsMapping,
          records: reportPartDetails.previewDataRecords,
          rawData: reportPartDetails.previewData
        })
      };
    },
    resetDefaultGridLine: reportPartContent.resetGridLineDefaultValue.bind(reportPartContent),
    fnResetBorderDefaultValue: reportPartContent.resetBorderDefaultValue.bind(reportPartContent),
    fnResetBackgroundColorDefaultValue: reportPartContent.resetBackgroundColorDefaultValue.bind(reportPartContent),
    fnResetXYPlaneDefaultValue: reportPartContent.resetXYPlaneDefaultValue.bind(reportPartContent),
    fnResetThresholdDefaultValue: reportPartContent.resetThresholdDefaultValue.bind(reportPartContent),
    fnResetRegressionLineDefaultValue: reportPartContent.resetRegressionLineDefaultValue.bind(reportPartContent),
    fnResetRefreshIntervalDefaultValue: reportPartContent.resetRefreshIntervalValue.bind(reportPartContent),
    fnResetLegendDefaultValue: reportPartContent.resetLegendDefaultValue.bind(reportPartContent),
    getBorder: () => ({
      'izEntire.borderColor': getProperties('izEntire.borderColor'),
      'izEntire.borderWidth': getProperties('izEntire.borderWidth'),
      'izEntire.borderStyle': getProperties('izEntire.borderStyle')
    }),
    hadSettingBorder: () => {
      const currentSetting = {
        'izEntire.borderColor': getProperties('izEntire.borderColor'),
        'izEntire.borderWidth': getProperties('izEntire.borderWidth'),
        'izEntire.borderStyle': getProperties('izEntire.borderStyle')
      };

      return !(
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.borderDefault) ||
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.borderNew)
      );
    },
    getBackgroundColor: () => ({
      'izEntire.backgroundColor': getProperties('izEntire.backgroundColor'),
      'izPlot.backgroundColor': getProperties('izPlot.backgroundColor')
    }),
    hadSettingBackgroundColor: () => {
      const currentSetting = {
        'izEntire.backgroundColor': getProperties('izEntire.backgroundColor'),
        'izPlot.backgroundColor': getProperties('izPlot.backgroundColor')
      };

      return !(
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.backGroundColorDefault) ||
        isEqual(currentSetting, DEFAULT_SETTING_VALUE.backGroundColorNew)
      );
    },
    getXYPlaneSettings: () => ({
      xAxisTitleOption: reportPartContent.getXAxisTitleOption(),
      yAxisTitleOption: reportPartContent.getYAxisTitleOption(),
      labelsElements: reportPartContent.labelsElements,
      valuesElements: reportPartContent.valuesElements,
      separatorsElements: reportPartContent.separatorsElements,
      metricAllOption: getProperties('metricAllOption')
    }),
    hadSettingXYPlane: () => {
      const { labelsElements, valuesElements } = reportPartContent;
      const hasSettingAxis = elements => {
        const elementHasSetting = filter(item => {
          const axis = get('properties.otherProps.axis', item);
          return (
            axis &&
            (axis['izTitle.enabled'] ||
              (item.previewDataTypeByFunction === DATA_TYPES.DATE_TIME
                ? !isNil(axis.izTickInterval) && axis.izTickInterval != DEFAULT_SETTING_VALUE.tickInterval
                : !isNil(axis.izTickInterval)) ||
              !isNil(axis.izStartingPoint))
          );
        }, elements);
        return !isEmpty(elementHasSetting);
      };

      return hasSettingAxis(labelsElements) || hasSettingAxis(valuesElements);
    },
    getGridLines: () => {
      const gridLine = getProperties('izGridLine') || {};
      return { xAxis: gridLine.xAxis || {}, yAxis: gridLine.yAxis || {} };
    },
    getColorTheme: () => {
      const properties = getProperties() || {};
      return { colorPalette: properties.colorTheme, isDefaultTheme: properties.isDefaultTheme };
    },
    hasSettingGridLine: () => {
      const gridLine = getProperties('izGridLine') || {};

      return (
        (!isNil(gridLine.xAxis) &&
          !isEmpty(gridLine.xAxis) &&
          gridLine.xAxis !== DEFAULT_SETTING_VALUE.gridLineDefault) ||
        (!isNil(gridLine.yAxis) && !isEmpty(gridLine.yAxis) && gridLine.yAxis !== DEFAULT_SETTING_VALUE.gridLineDefault)
      );
    },
    getLegendSettings: () => ({
      'izLegend.visibility': getProperties('izLegend.visibility'),
      'izLegend.horizontalAlign': getProperties('izLegend.horizontalAlign'),
      'izLegend.verticalAlign': getProperties('izLegend.verticalAlign'),
      'izLegend.borderColor': getProperties('izLegend.borderColor'),
      'izLegend.borderWidth': getProperties('izLegend.borderWidth'),
      'izLegend.fontStyling': getProperties('izLegend.fontStyling'),
      'izLegend.backgroundColor': getProperties('izLegend.backgroundColor'),
      'izLegend.borderVisible': getProperties('izLegend.borderVisible')
    }),
    hadSettingLegend: () => getProperties('izLegend.visibility'),
    getThresholdSettings: () => ({
      xAxisTitleOption: reportPartContent.getXAxisTitleOption(true),
      yAxisTitleOption: reportPartContent.getYAxisTitleOption(true, true),
      labelsElements: reportPartContent.labelsElements,
      valuesElements: reportPartContent.valuesElements,
      separatorsElements: reportPartContent.separatorsElements,
      metricAllPlotLinesOption: getProperties('metricAllPlotLinesOption'),
      allDynamicThresholdLine: getProperties('allDynamicThresholdLine')
    }),
    hadSettingThreshold: () => {
      const labelsElements = reportPartContent.labelsElements;
      const valuesElements = reportPartContent.valuesElements;
      const chartType = get('properties.chartType', reportPartContent);
      const notSupportDynamic = chartType === 'Bubble' || chartType === 'Scatter';
      const notSupportALL = isChartType(chartType, CHART_TYPE.Combination, CHART_TYPE.Waterfall);
      const metricAllPlotLinesOption = !notSupportALL && !notSupportDynamic ? getProperties('metricAllPlotLinesOption') : [];
      const allDynamicThresholdLine  = !notSupportALL && !notSupportDynamic ? getProperties('allDynamicThresholdLine'): [];

      // here
      const hasThreshold = elements => {
        const elementHasSetting = flow(
          flatMap(element => element.properties.otherProps),
          filter(otherProps => {
            return (
              (otherProps.threshold && !isEmpty(otherProps.threshold)) ||
              (!notSupportDynamic && (otherProps.dynamicThresholds && !isEmpty(otherProps.dynamicThresholds)))
            );
          })
        )(elements);
        return !isEmpty(elementHasSetting);
      };

      return (
        hasThreshold(labelsElements) ||
        hasThreshold(valuesElements) ||
        (!isNil(metricAllPlotLinesOption) && !isEmpty(metricAllPlotLinesOption)) ||
        (!isNil(allDynamicThresholdLine) && !isEmpty(allDynamicThresholdLine))
      );
    },
    getRegressionLineSettings: () => ({}),
    hadSeparators: () => {
      return !isEmpty(reportPartContent.separatorsElements);
    },
    removeUseSeparator: () => {
      reportPartContent.resetSeparator(false);
      fieldStore.needUpdatePreview(reportPartDetails);
    },
    hadSettingRegressionLine: () => {
      return reportPartContent.hadSettingRegressionLine();
    },
    getDataRefreshInterval: () => getProperties('dataRefreshInterval'),
    hadSettingRefreshInterval: () => {
      const defaultSetting = DEFAULT_SETTING_VALUE.refreshIntervalDefault;
      const refreshSetting = getProperties('dataRefreshInterval');

      return (
        refreshSetting.value !== defaultSetting.value || refreshSetting.updateInterval !== defaultSetting.updateInterval
      );
    },
    filterRangeOptions: options => {
      const chartType = get('properties.chartType', reportPartContent);

      if (isChartType(chartType, CHART_TYPE.Column, CHART_TYPE.Bar)) {
        return filter(item => item.value !== 'rangeWithAverage', options);
      }

      return options;
    }
  };
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DefaultChartPropsMappingProps.js